import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { CustomTranslateLoader } from '@core/i18n/custom-translate-loader';
import { AnswerModuleService } from '@core/services/answer-module.service';
import { PAGE_TITLES, usePageTitle } from '@core/utils/page-title.util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AnswerModuleResponse } from '@shared/models/answer-module-response';
import { GDPRStatement } from '@shared/models/gdpr-statements';
import { QuestionAnswerResponseData } from '@shared/models/question-answer-response';
import {
  PrintComponent,
  PrintGdprComponent,
  PrintQuestionComponent,
  PrintView,
} from '@shared/models/views-models/print-view';
import { Question } from '@shared/models/views-models/question-view';
import { NewlineToBrPipe } from '@shared/pipes/new-line-to-br.pipe';
import { SafeHtmlPipe } from '@shared/pipes/safe-html.pipe';
import { SurveyFormComponent } from '../../../survey-management/components/survey-form/survey-form.component';

@Component({
  selector: 'app-print-page',
  imports: [
    CommonModule,
    SurveyFormComponent,
    TranslateModule,
    SafeHtmlPipe,
    NewlineToBrPipe,
  ],
  templateUrl: './print-page.component.html',
  styleUrl: './print-page.component.scss',
})
export class PrintPageComponent implements OnInit {
  constructor(
    private answerModuleService: AnswerModuleService,
    private route: ActivatedRoute,
    private translateLoader: CustomTranslateLoader,
    private translateService: TranslateService,
  ) {}
  response?: AnswerModuleResponse;
  components: PrintComponent[] = [];
  questions: Question[] = [];
  answers: QuestionAnswerResponseData[] = [];

  private readonly destroyRef = inject(DestroyRef);
  private setPageTitle = usePageTitle();

  ngOnInit(): void {
    this.setPageTitle(PAGE_TITLES.PRINT.key, PAGE_TITLES.PRINT.fallback);

    const showConditions =
      this.route.snapshot.queryParamMap.get('showConditions') || 'false';
    const language = this.route.snapshot.queryParamMap.get('language');
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const questId = params.get('questId');
        if (questId && showConditions) {
          this.answerModuleService
            .getPrintPreview(questId, showConditions, language)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => {
              const response = data.body as AnswerModuleResponse;
              if (response && response.view.type === 'PRINT') {
                this.response = response;
                const printData = response.view.data as PrintView['data'];
                this.components = this.getPrintableComponents(printData);
                this.questions =
                  this.components.find(this.isQuestionComponent)?.questions ??
                  printData.questions ??
                  [];
                this.addInPrintViewProperty(this.questions);
                this.answers = response?.response?.data ?? [];

                this.translateLoader
                  .getTranslation(this.response.language, [
                    'validation.',
                    'question.fileupload.legaltext',
                    'welcome.hiddenidentity.',
                    'thankyou.',
                    'gdpr.statement.',
                  ], this.response.fallbackLanguage)
                  .pipe(takeUntilDestroyed(this.destroyRef))
                  .subscribe((translation) => {
                    this.translateService.setTranslation(
                      this.response!.language,
                      translation,
                      true,
                    );
                    this.translateService.use(this.response!.language);
                  });
              }
            });
        }
      });
  }

  addInPrintViewProperty(questionsArray: Question[]): void {
    const isRelevantType = (type: string) =>
      type === 'MULTIPLE_CHOICE' || type === 'SINGLE_CHOICE';

    for (const question of questionsArray) {
      if (isRelevantType(question.type)) {
        question.inPrintView = true;
      } else if (question.type === 'MATRIX') {
        for (const rowQuestion of question.rows) {
          if (isRelevantType(rowQuestion.type)) {
            rowQuestion.inPrintView = true;
          }
        }
      } else if (question.type === 'MULTI_MATRIX') {
        for (const columnQuestion of question.columns) {
          for (const rowQuestion of columnQuestion.rows) {
            if (isRelevantType(rowQuestion.type)) {
              rowQuestion.inPrintView = true;
            }
          }
        }
      }
    }
  }

  getChildren(component: PrintComponent): PrintComponent[] {
    if ('components' in component && Array.isArray(component.components)) {
      return this.filterLanguageComponents(component.components);
    }

    return [];
  }

  getMessage(component: PrintComponent): string {
    return 'message' in component && typeof component.message === 'string'
      ? component.message
      : '';
  }

  getHiddenIdentityValue(component: PrintComponent): boolean | null | undefined {
    return 'hidden' in component ? component.hidden : undefined;
  }

  getGdprFields(statement: GDPRStatement): { label: string; value?: string }[] {
    return [
      {
        label: 'gdpr.statement.field.processingperiodcriteria.respondentname',
        value: this.formatRetentionDuration(
          statement.responseRetentionDuration,
          statement.responseRetentionDurationInterval,
        ),
      },
      {
        label: 'gdpr.statement.field.personaldatatypes.respondentname',
        value: statement.personalDataTypes,
      },
      {
        label: 'gdpr.statement.field.personaldatacategories.respondentname',
        value: statement.personalDataCategories,
      },
      {
        label: 'gdpr.statement.field.legalbasisforprocessing.respondentname',
        value: statement.legalBasisForProcessing,
      },
      {
        label: 'gdpr.statement.field.categoriesofrecipients.respondentname',
        value: statement.categoriesOfRecipients,
      },
      {
        label: 'gdpr.statement.field.transferofdataexternally.respondentname',
        value: statement.transferOfDataExternally,
      },
      {
        label: 'gdpr.statement.field.statutoryorcontractualreq.respondentname',
        value: statement.statutoryOrContractualReq,
      },
      {
        label: 'gdpr.statement.field.automateddecisionmaking.respondentname',
        value: statement.automatedDecisionMaking,
      },
      {
        label: 'gdpr.statement.field.datasubjectrights.respondentname',
        value: statement.dataSubjectRights,
      },
      {
        label: 'gdpr.statement.field.withdrawconsentinfo.respondentname',
        value: statement.withdrawConsentInfo,
      },
      {
        label: 'gdpr.statement.field.supervisoryauthorityinfo.respondentname',
        value: statement.supervisoryAuthorityInfo,
      },
      {
        label: 'gdpr.statement.field.dataprotectionofficer.respondentname',
        value: statement.dataProtectionOfficer,
      },
    ].filter((field) => !!field.value);
  }

  isQuestionComponent(component: PrintComponent): component is PrintQuestionComponent {
    return component.componentType === 'QUESTIONS';
  }

  isGdprComponent(component: PrintComponent): component is PrintGdprComponent {
    return component.componentType === 'GDPR_CONSENT';
  }

  private getPrintableComponents(data: PrintView['data']): PrintComponent[] {
    const components = this.orderPrintableComponents(
      this.filterLanguageComponents(data.components ?? []),
    );
    if (components.length > 0) {
      return components;
    }

    return data.questions?.length
      ? [{ componentType: 'QUESTIONS', questions: data.questions }]
      : [];
  }

  private orderPrintableComponents(components: PrintComponent[]): PrintComponent[] {
    const order: Record<string, number> = {
      PASSWORD: 0,
      GDPR_CONSENT: 1,
      INITIAL_INFORMATION: 2,
      QUESTIONS: 3,
      THANKYOU_INFORMATION: 4,
    };

    return [...components].sort(
      (first, second) =>
        (order[first.componentType] ?? 50) -
        (order[second.componentType] ?? 50),
    );
  }

  private filterLanguageComponents(components: PrintComponent[]): PrintComponent[] {
    return components
      .filter((component) => component.componentType !== 'CHOOSE_LANGUAGE')
      .map((component) => {
        if ('components' in component && Array.isArray(component.components)) {
          return {
            ...component,
            components: this.filterLanguageComponents(component.components),
          } as PrintComponent;
        }

        return component;
      });
  }

  private formatRetentionDuration(
    duration: number,
    interval?: string,
  ): string | undefined {
    if (duration && duration < 0) {
      return this.translateService.instant('gdpr.statement.retention.unlimited');
    }

    if (!duration || duration <= 0) {
      return undefined;
    }

    if (!interval) {
      return `${duration}`;
    }

    const key =
      duration > 1
        ? `gdpr.statement.retention.${interval.toLowerCase()}s`
        : `gdpr.statement.retention.${interval.toLowerCase()}`;
    const translatedInterval = this.translateService.instant(key);

    return `${duration} ${translatedInterval}`;
  }
}
